/**
 * Análise por setor: heatmap de 30 dias (setores × dias) — Nexora Grid.
 *
 * Criado em: 17/10/2026 · Última modificação: 17/10/2026 · Versão: 1.1.0
 *
 * Pedido do responsável do produto (17/10/2026): "quero um mapa de calor por setor, de 30 dias, com consumo total de
 * energia, produção de água, falhas, entre outros — um heatmap analítico por setor". Dados de
 * `GET /api/v1/analise/heatmap-setores` (agregação diária no hub); visual no padrão do heatmap do Grafana: escala de
 * cor por métrica, legenda com mínimo e máximo, total ou média por setor e por dia, valor no detalhe da célula.
 * v1.1 (retorno de 17/10/2026): clicar na célula abre o MICRO CENÁRIO que compõe a cor — o valor e a conta, a
 * contribuição de cada equipamento, o perfil hora a hora, as demais métricas do dia e os alarmes do dia.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ErroApi } from "../api";
import { useCco } from "./contexto";

type Metrica = { chave: string; rotulo: string; unidade: string; escala: string; valores: Record<string, (number | null)[]> };
type Detalhe = {
  setor: string; dia: string; metrica: string; horas_consideradas: number;
  resumo: Record<string, { rotulo: string; unidade: string; valor: number | null }>;
  composicao: { device: string; identificacao: string; tipo: string; valor: number; fatia_pct: number | null; conta: string }[];
  horario: { hora: number; valor: number | null }[]; unidade_horario: string;
  alarmes: { instante: string; identificacao: string; device: string; tipo: string; severidade: string; descricao: string }[];
};
type Heatmap = { dias: string[]; setores: string[]; metricas: Metrica[]; gerado_em: string };

// Escalas (paletas do Grafana): consumo em laranja, produção em azul, falhas do verde ao vermelho, pressão e nível
// do vermelho (baixo) ao azul/verde (adequado)
const ESCALAS: Record<string, string[]> = {
  consumo: ["#1f1d1a", "#5c3b14", "#a0571a", "#e0752d", "#ffb357"],
  producao: ["#14202b", "#173b5c", "#1f5f94", "#3a8bd0", "#8ab8ff"],
  alerta: ["#1b3a2a", "#4e6b1f", "#c4a21a", "#e0752d", "#e5383b"],
  pressao: ["#e5383b", "#e0752d", "#c4a21a", "#3fbf7f", "#2f8f63"],
  nivel: ["#e5383b", "#e0752d", "#c4a21a", "#3a8bd0", "#1f5f94"],
};
const MEDIA = new Set(["kwh_por_m3", "pressao_min_mca", "nivel_medio_pct"]);

function cor(escala: string[], t: number): string {
  const x = Math.max(0, Math.min(1, t)) * (escala.length - 1);
  const i = Math.min(escala.length - 2, Math.floor(x)), f = x - i;
  const a = escala[i].match(/\w\w/g)!.map((h) => parseInt(h, 16)), b = escala[i + 1].match(/\w\w/g)!.map((h) => parseInt(h, 16));
  return `rgb(${a.map((v, k) => Math.round(v + (b[k] - v) * f)).join(",")})`;
}

const fmt = (v: number | null, casas = 1) => (v === null ? "—" : v.toLocaleString("pt-BR", { maximumFractionDigits: casas }));
const SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

export function Analise() {
  const { empresa } = useCco();
  const [dias, setDias] = useState(30);
  const [dados, setDados] = useState<Heatmap | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [chave, setChave] = useState("energia_kwh");
  const [foco, setFoco] = useState<{ setor: string; dia: number } | null>(null);
  const [selecionada, setSelecionada] = useState<{ setor: string; dia: number } | null>(null);

  useEffect(() => {
    if (!empresa) return;
    setDados(null);
    api<Heatmap>(`/v1/analise/heatmap-setores?empresa=${empresa}&dias=${dias}`).then((r) => { setDados(r); setErro(null); })
      .catch((e) => setErro(e instanceof ErroApi ? e.message : "falha de rede"));
  }, [empresa, dias]);

  const metrica = dados?.metricas.find((m) => m.chave === chave) ?? dados?.metricas[0];
  const media = metrica ? MEDIA.has(metrica.chave) : false;
  const { min, max } = useMemo(() => {
    const todos = metrica ? Object.values(metrica.valores).flat().filter((v): v is number => v !== null) : [];
    return { min: todos.length ? Math.min(...todos) : 0, max: todos.length ? Math.max(...todos) : 1 };
  }, [metrica]);
  const agregar = (lista: (number | null)[]) => {
    const v = lista.filter((x): x is number => x !== null);
    if (!v.length) return null;
    return media ? v.reduce((a, b) => a + b, 0) / v.length : v.reduce((a, b) => a + b, 0);
  };

  if (erro) return <section className="analise"><p className="nx-suave">Análise indisponível: {erro}</p></section>;
  if (!dados || !metrica) return <section className="analise"><p className="nx-suave">{dados ? "sem dados no período" : "calculando o heatmap…"}</p></section>;
  const escala = ESCALAS[metrica.escala] ?? ESCALAS.consumo;
  const setores = dados.setores.filter((s) => metrica.valores[s]?.some((v) => v !== null));
  const t = (v: number) => (max - min < 1e-9 ? 0.5 : (v - min) / (max - min));
  const focado = foco ? metrica.valores[foco.setor]?.[foco.dia] ?? null : null;

  return (
    <section className="analise" aria-label="Análise por setor">
      <div className="nx-cartao">
        <h3>Heatmap por setor · {dias} dias</h3>
        <div className="controles-analise">
          <span className="metricas" role="group" aria-label="Métrica">
            {dados.metricas.map((m) => (
              <button key={m.chave} type="button" className={`nx-botao${m.chave === metrica.chave ? " ativo" : ""}`} aria-pressed={m.chave === metrica.chave}
                onClick={() => setChave(m.chave)}>{m.rotulo}</button>
            ))}
          </span>
          <span className="seletor-periodo" role="group" aria-label="Período">
            {[7, 15, 30].map((d) => <button key={d} type="button" className={`nx-botao${d === dias ? " ativo" : ""}`} onClick={() => setDias(d)}>{d} dias</button>)}
          </span>
        </div>
        <div className="legenda-heatmap">
          <span>{fmt(min)} {metrica.unidade}</span>
          <span className="gradiente" style={{ background: `linear-gradient(90deg, ${escala.join(",")})` }} />
          <span>{fmt(max)} {metrica.unidade}</span>
          <span className="nx-suave detalhe-celula" aria-live="polite">
            {foco ? `${foco.setor} · ${new Date(dados.dias[foco.dia] + "T12:00").toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" })}: ${fmt(focado, 2)} ${metrica.unidade}`
              : "passe o mouse para ver o valor · clique para o micro cenário"}
          </span>
        </div>
        <div className="grade-heatmap" style={{ gridTemplateColumns: `minmax(90px, auto) repeat(${dados.dias.length}, minmax(22px, 1fr)) minmax(80px, auto)` }}
          role="table" aria-label={`${metrica.rotulo} por setor e dia`} onMouseLeave={() => setFoco(null)}>
          <span className="canto" role="columnheader">setor</span>
          {dados.dias.map((d) => {
            const dt = new Date(d + "T12:00");
            return <span key={d} className={`cab-dia${dt.getDay() === 0 || dt.getDay() === 6 ? " fim-semana" : ""}`} role="columnheader">
              {SEMANA[dt.getDay()][0]}<br />{String(dt.getDate()).padStart(2, "0")}</span>;
          })}
          <span className="cab-total" role="columnheader">{media ? "média" : "total"}</span>
          {setores.map((s) => (
            <div key={s} className="linha-heatmap" role="row">
              <span className="nome-setor" role="rowheader">{s}</span>
              {metrica.valores[s].map((v, i) => (
                <span key={i} role="cell" className={`celula${foco?.setor === s && foco.dia === i ? " focada" : ""}${selecionada?.setor === s && selecionada.dia === i ? " selecionada" : ""}`}
                  style={{ background: v === null ? "transparent" : cor(escala, t(v)) }}
                  title={`${s} · ${dados.dias[i]}: ${fmt(v, 2)} ${metrica.unidade}`} aria-label={`${s} ${dados.dias[i]}: ${fmt(v, 2)}`}
                  onMouseEnter={() => setFoco({ setor: s, dia: i })}
                  onClick={() => v !== null && setSelecionada({ setor: s, dia: i })} tabIndex={v === null ? -1 : 0}
                  onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && v !== null) { e.preventDefault(); setSelecionada({ setor: s, dia: i }); } }} />
              ))}
              <span className="total-setor nx-valor">{fmt(agregar(metrica.valores[s]))}</span>
            </div>
          ))}
          <span className="nome-setor rodape" role="rowheader">{media ? "média" : "total"}</span>
          {dados.dias.map((d, i) => <span key={d} className="total-dia nx-valor">{fmt(agregar(setores.map((s) => metrica.valores[s][i])), 0)}</span>)}
          <span className="total-setor nx-valor">{fmt(agregar(setores.flatMap((s) => metrica.valores[s])))}</span>
        </div>
        {selecionada && empresa && (
          <MicroCenario empresa={empresa} setor={selecionada.setor} dia={dados.dias[selecionada.dia]} metrica={metrica.chave}
            escala={escala} aoMudarMetrica={setChave} aoFechar={() => setSelecionada(null)} />
        )}
        <p className="nx-suave nota-analise">
          Agregação diária no fuso de Brasília; hoje conta as horas decorridas. Energia = potência média × horas; água = vazão média × horas;
          eficiência = energia ÷ água produzida. Atualiza a cada 10 min.
        </p>
      </div>
    </section>
  );
}

const ROTULO_TIPO: Record<string, string> = { bomba: "bomba", poco_profundo: "poço", elevatoria: "elevatória", macromedidor: "macromedidor",
  reservatorio: "reservatório", vrp: "VRP" };

/** O que compõe a cor da célula: valor e conta, equipamentos, perfil horário, demais métricas e alarmes do dia. */
function MicroCenario({ empresa, setor, dia, metrica, escala, aoMudarMetrica, aoFechar }: {
  empresa: string; setor: string; dia: string; metrica: string; escala: string[]; aoMudarMetrica: (m: string) => void; aoFechar: () => void;
}) {
  const [d, setD] = useState<Detalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    setD(null); setErro(null);
    api<Detalhe>(`/v1/analise/heatmap-setores/detalhe?empresa=${empresa}&setor=${encodeURIComponent(setor)}&dia=${dia}&metrica=${metrica}`)
      .then(setD).catch((e) => setErro(e instanceof ErroApi ? e.message : "falha de rede"));
  }, [empresa, setor, dia, metrica]);
  const dataBruta = new Date(dia + "T12:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
  const data = dataBruta.charAt(0).toUpperCase() + dataBruta.slice(1);
  const atual = d?.resumo[metrica];
  const valoresHora = (d?.horario ?? []).map((h) => h.valor ?? 0);
  const maxHora = Math.max(1e-9, ...valoresHora);
  const maxComp = Math.max(1e-9, ...(d?.composicao ?? []).map((c) => c.valor));
  return (
    <section className="micro-cenario" aria-label={`Micro cenário de ${setor} em ${dia}`}>
      <header>
        <h4>{setor} · {data}</h4>
        {atual && <span className="valor-celula nx-valor">{atual.rotulo}: <b>{fmt(atual.valor, 2)}</b> {atual.unidade}</span>}
        <button type="button" className="nx-botao" onClick={aoFechar} aria-label="Fechar o micro cenário">fechar</button>
      </header>
      {erro && <p className="nx-suave">Não foi possível abrir o detalhe: {erro}</p>}
      {!d && !erro && <p className="nx-suave">montando o micro cenário…</p>}
      {d && (
        <div className="grade-micro">
          <div className="bloco-micro resumo-dia">
            <h5>O dia no setor</h5>
            <div className="chips-resumo">
              {Object.entries(d.resumo).map(([k, r]) => (
                <button key={k} type="button" className={`chip-resumo${k === metrica ? " ativo" : ""}`} onClick={() => aoMudarMetrica(k)}>
                  <small>{r.rotulo}</small><b className="nx-valor">{fmt(r.valor, 1)}</b> <small>{r.unidade}</small>
                </button>
              ))}
            </div>
            <p className="nx-suave">{d.horas_consideradas < 24 ? `dia em andamento: ${d.horas_consideradas.toFixed(1)} h consideradas` : "dia completo (24 h)"}</p>
          </div>
          <div className="bloco-micro">
            <h5>O que compõe o valor ({d.composicao.length} equipamento{d.composicao.length === 1 ? "" : "s"})</h5>
            {d.composicao.length ? (
              <table className="tabela-composicao">
                <thead><tr><th>Equipamento</th><th>Valor</th><th>Participação</th></tr></thead>
                <tbody>
                  {d.composicao.map((c) => (
                    <tr key={c.device}>
                      <td><Link to={`/cco/equipamento/${c.device}`}>{c.identificacao}</Link> <small className="nx-suave">{ROTULO_TIPO[c.tipo] ?? c.tipo}</small></td>
                      <td className="nx-valor">{fmt(c.valor, 2)} {atual?.unidade}<br /><small className="nx-suave">{c.conta}</small></td>
                      <td className="celula-barra">
                        <span className="barra" style={{ width: `${(c.valor / maxComp) * 100}%`, background: escala[3] }} />
                        <small>{c.fatia_pct !== null ? `${fmt(c.fatia_pct, 1)} %` : ""}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="nx-suave">nenhum equipamento contribuiu para esta métrica no dia</p>}
          </div>
          <div className="bloco-micro">
            <h5>Hora a hora ({d.unidade_horario})</h5>
            <svg className="perfil-horario" viewBox="0 0 480 140" role="img" aria-label="perfil hora a hora">
              {d.horario.map((h, i) => {
                const alt = h.valor === null ? 0 : (h.valor / maxHora) * 110;
                return (
                  <g key={h.hora}>
                    <rect x={i * 20 + 2} y={120 - alt} width={16} height={Math.max(alt, h.valor === null ? 0 : 1)} rx={2}
                      fill={h.valor === null ? "transparent" : escala[Math.min(4, 1 + Math.round((h.valor / maxHora) * 3))]}>
                      <title>{`${String(h.hora).padStart(2, "0")}h: ${fmt(h.valor, 2)} ${d.unidade_horario}`}</title>
                    </rect>
                    {h.hora % 3 === 0 && <text x={i * 20 + 10} y={136} textAnchor="middle">{String(h.hora).padStart(2, "0")}h</text>}
                  </g>
                );
              })}
              <text x={478} y={10} textAnchor="end">máx {fmt(maxHora, 1)}</text>
              <line x1={0} x2={480} y1={120} y2={120} className="eixo" />
            </svg>
          </div>
          <div className="bloco-micro">
            <h5>Alarmes do dia ({d.alarmes.length})</h5>
            {d.alarmes.length ? (
              <ul className="alarmes-dia">
                {d.alarmes.slice(0, 15).map((a, i) => (
                  <li key={i}><span className="nx-valor">{new Date(a.instante).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
                    {" "}<b>{a.identificacao}</b> · {a.tipo.replace(/_/g, " ").toLowerCase()} <small className="nx-suave">({a.severidade.toLowerCase()})</small></li>
                ))}
                {d.alarmes.length > 15 && <li className="nx-suave">… e mais {d.alarmes.length - 15}</li>}
              </ul>
            ) : <p className="nx-suave">nenhum alarme no setor neste dia</p>}
          </div>
        </div>
      )}
    </section>
  );
}
